// src/components/CaseManagement.js

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import 'animate.css';
import { usePermissions } from '../App'; // Import the permissions hook
import { normalizeApiNextUrl } from '../utils/normalizeApiNextUrl';
import ConflictCheckModal from './ConflictCheckModal';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { useConfirm } from './ConfirmProvider';

function CaseManagement({
  cases = [], clients = [], users = [], partners = [], associatesList = [], paralegalsList = [],
  authAxios, fetchCases, fetchTasks, handleDelete, caseTypeOptions, caseStatusOptions, casePhaseOptions, caseStageOptions, caseTypeStagesMap, getStagesForType, navigateToItem, setActiveTab,
  currentUser, navigateTo, setNavigateTo, showFormMessage,
}) {
  const permissions = usePermissions(); // Use the permissions hook
  const navigate = useNavigate();
  const confirm = useConfirm();

  // showToast: use global showFormMessage when available, else fallback to local toast
  const showToast = useCallback((message, type = 'success') => {
    if (typeof showFormMessage === 'function') {
      showFormMessage(type, message);
      return;
    }
    console.warn('Toast:', type, message);
  }, [showFormMessage]);

  const [casesViewMode, setCasesViewMode] = useState('board'); // Default to board view
  const [showAddCaseForm, setShowAddCaseForm] = useState(false);
  const [showEditCaseModal, setShowEditCaseModal] = useState(false);
  const [showCaseDetailsModal, setShowCaseDetailsModal] = useState(false);
  const [selectedCaseDetails, setSelectedCaseDetails] = useState(null);
  const [showConflictCheckModal, setShowConflictCheckModal] = useState(false);
  const [currentConflictCheckResult, setCurrentConflictCheckResult] = useState(null);
  const [pendingCaseData, setPendingCaseData] = useState(null);
  const [caseRecords, setCaseRecords] = useState({
    documents: [],
    timeEntries: [],
    expenseEntries: [],
    invoices: [],
    communicationLogs: [],
    tasks: [],
  });
  const [isFetchingRecords, setIsFetchingRecords] = useState(false);
  const [activeDetailsTab, setActiveDetailsTab] = useState('overview');
  const [selectedDocumentIds, setSelectedDocumentIds] = useState([]);
  const [showSubmitToCourtConfirm, setShowSubmitToCourtConfirm] = useState(false);

  const toggleSelectedDocument = useCallback((documentId) => {
    setSelectedDocumentIds((prev) => {
      if (prev.includes(documentId)) return prev.filter((id) => id !== documentId);
      return [...prev, documentId];
    });
  }, []);

  const toggleSelectAllDocuments = useCallback(() => {
    setSelectedDocumentIds((prev) => {
      const allIds = (caseRecords.documents || []).map((d) => d.id);
      if (prev.length === allIds.length) return [];
      return allIds;
    });
  }, [caseRecords.documents]);

  const openSubmitToCourtConfirm = useCallback(() => {
    if (!selectedCaseDetails) {
      showToast('No case selected.', 'danger');
      return;
    }
    if (!selectedDocumentIds.length) {
      showToast('Select at least one document to submit.', 'danger');
      return;
    }
    setShowSubmitToCourtConfirm(true);
  }, [caseRecords.documents, selectedCaseDetails, selectedDocumentIds, showToast]);

  const confirmSubmitDocumentsToCourt = useCallback(() => {
    if (!selectedCaseDetails) {
      showToast('No case selected.', 'danger');
      return;
    }
    const selectedDocs = (caseRecords.documents || []).filter((d) => selectedDocumentIds.includes(d.id));
    if (!selectedDocs.length) {
      showToast('Select at least one document to submit.', 'danger');
      setShowSubmitToCourtConfirm(false);
      return;
    }
    showToast(
      `Placeholder: would submit ${selectedDocs.length} document(s) to Court for “${selectedCaseDetails.title}”.`,
      'success'
    );
    setShowSubmitToCourtConfirm(false);
    setSelectedDocumentIds([]);
  }, [caseRecords.documents, selectedCaseDetails, selectedDocumentIds, showToast]);

  // Search, Filter, and Sort States
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'date_opened', direction: 'descending' });
  // Restore saved filters/view from localStorage on mount
  useEffect(() => {
    try {
      const persisted = JSON.parse(localStorage.getItem('cases.ui') || '{}');
      if (persisted.viewMode) setCasesViewMode(persisted.viewMode);
      if (typeof persisted.searchTerm === 'string') setSearchTerm(persisted.searchTerm);
      if (typeof persisted.statusFilter === 'string') setStatusFilter(persisted.statusFilter);
      if (typeof persisted.typeFilter === 'string') setTypeFilter(persisted.typeFilter);
      if (typeof persisted.stageFilter === 'string') setStageFilter(persisted.stageFilter);
      if (persisted.sortConfig && persisted.sortConfig.key) setSortConfig(persisted.sortConfig);
    } catch (_) {}
  }, []);

  // Persist filters/view to localStorage when they change
  useEffect(() => {
    const payload = {
      viewMode: casesViewMode,
      searchTerm,
      statusFilter,
      typeFilter,
      stageFilter,
      sortConfig,
    };
    try { localStorage.setItem('cases.ui', JSON.stringify(payload)); } catch (_) {}
  }, [casesViewMode, searchTerm, statusFilter, typeFilter, stageFilter, sortConfig]);


  const [newCase, setNewCase] = useState({
    client: '', title: '', description: '', case_type: 'Contract', status: 'Open',
    case_stage: (caseTypeStagesMap && caseTypeStagesMap['Contract'] ? caseTypeStagesMap['Contract'][0] : 'Instruction & Scope'), phase: 'Initiation',
    date_opened: new Date().toISOString().split('T')[0],
    date_closed: '',
    partner: '',
    associates: [],
    paralegals: [],
    parent_case: '', court_name: '', jurisdiction: '', court_case_number: '',
    opposing_counsel: '',
    parties_involved: ''
  });

  const [editingCase, setEditingCase] = useState(null);
  const [caseConflictMessages, setCaseConflictMessages] = useState([]);

  const staffUsers = useMemo(() => {
    return (users || []).filter(user => user.is_staff);
  }, [users]);

    const filteredAndSortedCases = useMemo(() => {
    let filteredCases = Array.isArray(cases) ? [...cases] : [];

    if (searchTerm) {
      filteredCases = filteredCases.filter(c =>
        c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.client_full_name && c.client_full_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (c.case_number && c.case_number.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    if (statusFilter) {
      filteredCases = filteredCases.filter(c => c.status === statusFilter);
    }
    if (typeFilter) {
        filteredCases = filteredCases.filter(c => c.case_type === typeFilter);
    }
    if (stageFilter) {
        filteredCases = filteredCases.filter(c => c.case_stage === stageFilter);
    }


    filteredCases.sort((a, b) => {
      if (!a[sortConfig.key] || !b[sortConfig.key]) return 0;

      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });

    return filteredCases;
  }, [cases, searchTerm, statusFilter, typeFilter, stageFilter, sortConfig]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };


  const handleCaseInputChange = (e) => {
    const { name, value, multiple, selectedOptions } = e.target;
    if (multiple) {
      const values = Array.from(selectedOptions).map(o => o.value);
      setNewCase({ ...newCase, [name]: values });
    } else if (name === 'case_type' && getStagesForType) {
      // When case type changes, auto-set the first stage for that type
      const firstStageOptions = getStagesForType(value);
      const firstStage = firstStageOptions.length > 0 ? firstStageOptions[0].value : '';
      setNewCase({ ...newCase, case_type: value, case_stage: firstStage });
    } else {
      setNewCase({ ...newCase, [name]: value });
    }
  };

  const handleEditingCaseChange = (e) => {
    const { name, value, multiple, selectedOptions } = e.target;
    if (multiple) {
      const values = Array.from(selectedOptions).map(o => o.value);
      setEditingCase(prev => ({ ...prev, [name]: values }));
    } else {
      setEditingCase(prev => ({ ...prev, [name]: value }));
    }
  };

  const parsePartiesInvolved = (partiesString) => {
    if (!partiesString) return [];
    return partiesString.split(',').map(name => name.trim()).filter(name => name !== '');
  };

  const handleDeleteClick = async (caseId) => {
    const ok = await confirm({
      title: 'Confirm deletion',
      message: 'Are you sure you want to delete this case? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (!ok) return;
    handleDeleteCase(caseId);
  };

  const handleDeleteCase = useCallback(async (caseId) => {
      try {
          await authAxios.delete(`cases/${caseId}/`);
          showToast('Case deleted successfully!', 'success');
          if (fetchCases) fetchCases();
      } catch (error) {
          console.error(`Error deleting Case:`, error.response ? error.response.data : error.message);
          showToast('Failed to delete Case.', 'danger');
      }
  }, [authAxios, fetchCases, showToast]); // Added showToast dependency


  // --- MODIFICATION: Added skipConflictCheck parameter ---
  const handleCaseSubmission = useCallback(async (caseData, isEdit = false, caseId = null, skipConflictCheck = false) => {
    setCaseConflictMessages([]);

    try {
      const partiesToSubmit = parsePartiesInvolved(caseData.parties_involved);

      // --- MODIFICATION: Conditionally skip conflict check ---
      if (!skipConflictCheck) {
        let conflictResponse;
        if (isEdit) {
            // Check conflict specifically for the case being edited
            conflictResponse = await authAxios.post(`cases/${caseId}/check-conflict/`, {
                parties_to_check: partiesToSubmit,
            });
        } else {
            // Perform a general conflict check before creating a case
            conflictResponse = await authAxios.post('conflict-checks/', {
                party_name_checked: partiesToSubmit.join(', '),
                details: { parties_to_check: partiesToSubmit },
                client: caseData.client || null,
                // Do not associate with a case ID yet, as it doesn't exist
            });
        }

        if (conflictResponse.data.status === 'POTENTIAL') {
            setCurrentConflictCheckResult(conflictResponse.data);
            // Save the *resolved* conflict check ID if available (from edit check)
            // or the *new* conflict check ID (from create check)
            const conflictCheckId = conflictResponse.data.id;
            setPendingCaseData({ caseData, isEdit, caseId, conflictCheckId });
            setShowConflictCheckModal(true);
            showToast('Potential conflict of interest detected. Please review and resolve.', 'warning');
            return; // Stop processing until conflict is resolved
        }
      }
      // --- END MODIFICATION ---

      // Proceed with saving the case if check is skipped or clear
      // Normalize payload to match backend (M2M for associates/paralegals)
      const payload = {
        ...caseData,
        client: caseData.client,
        partner: caseData.partner || null,
        associates: Array.isArray(caseData.associates) ? caseData.associates.map(Number) : (caseData.associates ? [Number(caseData.associates)] : []),
        paralegals: Array.isArray(caseData.paralegals) ? caseData.paralegals.map(Number) : (caseData.paralegals ? [Number(caseData.paralegals)] : []),
        parent_case: caseData.parent_case || null,
        date_closed: caseData.date_closed || null,
        court_name: caseData.court_name || null,
        jurisdiction: caseData.jurisdiction || null,
        court_case_number: caseData.court_case_number || null,
        opposing_counsel: caseData.opposing_counsel || null,
        parties_involved: partiesToSubmit
      };

      // If skipping conflict check because user waived it, include a force flag
      if (skipConflictCheck) {
        payload.force = true;
      }

      if (isEdit) {
        await authAxios.patch(`cases/${caseId}/`, payload);
        showToast('Case updated successfully!', 'success');
        setShowEditCaseModal(false);
        setEditingCase(null);
      } else {
        const createResp = await authAxios.post('cases/', payload);
        // Defensive: some deployments previously returned 2xx even when creation was blocked.
        if (createResp?.data && createResp.data.conflicts && !createResp.data.id) {
          showToast('Case was not created: potential conflict(s) detected. Please review and resolve the conflict check, then try again.', 'warning');
          return;
        }
        showToast('Case added successfully!', 'success');
        setShowAddCaseForm(false);
        setNewCase({
          client: '', title: '', description: '', case_type: 'Contract', status: 'Open',
          case_stage: (caseTypeStagesMap && caseTypeStagesMap['Contract'] ? caseTypeStagesMap['Contract'][0] : 'Instruction & Scope'), phase: 'Initiation',
          date_opened: new Date().toISOString().split('T')[0],
          date_closed: '',
          partner: '', associate: '', paralegal: '',
          parent_case: '', court_name: '', jurisdiction: '', court_case_number: '',
          opposing_counsel: '',
          parties_involved: ''
        });
      }
      // After successful submission, refetch both cases and tasks
      await fetchCases();
      if (typeof fetchTasks === 'function') {
        await fetchTasks();
      }
      setPendingCaseData(null); // Clear pending data after successful save
    } catch (err) {
      console.error('Error in case submission:', err);
      // Backend may block creation on conflicts (409) and return a ConflictCheck object.
      // If so, open the existing ConflictCheckModal resolution flow.
      if (err?.response?.status === 409) {
        const data = err?.response?.data;
        if (data && data.status === 'POTENTIAL' && data.id) {
          setCurrentConflictCheckResult(data);
          setPendingCaseData({ caseData, isEdit, caseId, conflictCheckId: data.id });
          setShowConflictCheckModal(true);
          showToast('Potential conflict of interest detected. Please review and resolve.', 'warning');
          return;
        }
        if (data?.conflicts) {
          showToast('Case was not created: potential conflict(s) detected. Please review the conflict details and try again with an approved waiver if appropriate.', 'warning');
          return;
        }
      }
      // It's unlikely to get conflict_of_interest here now, but keeping for safety
      if (err.response && err.response.data && err.response.data.conflict_of_interest) {
        setCaseConflictMessages(err.response.data.conflict_of_interest);
        showToast('Potential conflict of interest detected. Please review the details below.', 'danger');
      } else {
        const errorDetails = err.response ? JSON.stringify(err.response.data) : err.message;
        showToast(`Failed to submit case. Details: ${errorDetails}`, 'danger');
      }
      // Do not clear pendingCaseData on error, user might retry
    }
  }, [authAxios, fetchCases, fetchTasks, showToast]); // Added showToast dependency


  const handleAddCaseSubmit = useCallback((e) => {
    e.preventDefault();
    handleCaseSubmission(newCase);
  }, [newCase, handleCaseSubmission]);

  const handleEditCaseSubmit = useCallback((e) => {
    e.preventDefault();
    if (!editingCase) return;
    handleCaseSubmission(editingCase, true, editingCase.id);
  }, [editingCase, handleCaseSubmission]);

  // --- MODIFICATION: Handle conflict resolution ---
  const handleConflictResolved = useCallback((updatedConflictCheck) => {
    // Update the state immediately to reflect the resolved status in the modal if it remains open
    setCurrentConflictCheckResult(updatedConflictCheck);

    if (updatedConflictCheck.status === 'WAIVED') {
      showToast('Conflict waived. Proceeding with case submission...', 'info');
      if (pendingCaseData) {
        // Call submission again, but skip the conflict check this time
        handleCaseSubmission(
          pendingCaseData.caseData,
          pendingCaseData.isEdit,
          pendingCaseData.caseId,
          true // skipConflictCheck = true
        );
      }
      setShowConflictCheckModal(false); // Close modal after initiating save
    } else if (updatedConflictCheck.status === 'DECLINED') {
      showToast('Engagement declined due to conflict of interest.', 'warning');
      setShowConflictCheckModal(false);
      setPendingCaseData(null); // Clear pending data as we are not proceeding
      // Optionally close the add/edit form as well
      if (showAddCaseForm) setShowAddCaseForm(false);
      if (showEditCaseModal) setShowEditCaseModal(false);
    }
    // No action needed if status is still 'POTENTIAL' (e.g., user just added notes)
  }, [handleCaseSubmission, pendingCaseData, showAddCaseForm, showEditCaseModal, showToast]); // Added showToast

  const openEditCaseModal = useCallback((_case) => {
    const normalizeM2M = (val) => {
      if (!val) return [];
      if (Array.isArray(val)) {
        return val.map(v => (typeof v === 'object' ? v.id : v));
      }
      return [val];
    };

    setEditingCase({
      ..._case,
      date_opened: _case.date_opened || '',
      date_closed: _case.date_closed || '',
      client: _case.client?.id || _case.client,
      partner: _case.partner?.id || _case.partner || '',
      associates: normalizeM2M(_case.associates || _case.associate),
      paralegals: normalizeM2M(_case.paralegals || _case.paralegal),
      parent_case: _case.parent_case?.id || _case.parent_case || '',
      parties_involved: Array.isArray(_case.parties_involved) ? _case.parties_involved.join(', ') : (_case.parties_involved || '') // Ensure it's a string
    });
    setShowEditCaseModal(true);
    setShowCaseDetailsModal(false); // Close details modal if open
    setCaseConflictMessages([]);
  }, []);

  const closeEditCaseModal = useCallback(() => {
    setEditingCase(null);
    setShowEditCaseModal(false);
    setCaseConflictMessages([]);
    setPendingCaseData(null); // Ensure pending data is cleared when manually closing
  }, []);

  // --- NEW: Helper function to fetch all pages from a paginated API endpoint ---
  const fetchAllPages = useCallback(async (url, resourceName) => {
    let results = [];
    let nextUrl = url;
    while (nextUrl) {
        try {
        const response = await authAxios.get(normalizeApiNextUrl(nextUrl));
            const data = response.data;
            if (data && Array.isArray(data.results)) {
                results = results.concat(data.results);
          nextUrl = normalizeApiNextUrl(data.next); // Get the URL for the next page
            } else if (Array.isArray(data)) {
                // Handle non-paginated responses
                results = results.concat(data);
                nextUrl = null;
            } else {
                // No more results or unexpected format
                nextUrl = null;
            }
        } catch (error) {
            console.error(`Error fetching data from ${nextUrl}:`, error);
            showToast(`Failed to fetch all ${resourceName}. The list may be incomplete.`, 'danger');
            break; // Exit loop on error
        }
    }
    return results;
  }, [authAxios, showToast]); // Dependencies
  
  // --- MODIFIED: fetchCaseRecords now uses fetchAllPages ---
  const fetchCaseRecords = useCallback(async (caseId) => {
    setIsFetchingRecords(true);
    setCaseRecords({
      documents: [],
      timeEntries: [],
      expenseEntries: [],
      invoices: [],
      communicationLogs: [],
      tasks: [],
    });
    try {
      const [
        documents,
        timeEntries,
        expenseEntries,
        invoices,
        communicationLogs,
        tasks,
      ] = await Promise.all([
        fetchAllPages(`documents/?case=${caseId}`, 'documents'),
        fetchAllPages(`time-entries/?case=${caseId}`, 'time entries'),
        fetchAllPages(`expense-entries/?case=${caseId}`, 'disbursements'),
        fetchAllPages(`invoices/?case=${caseId}`, 'invoices'),
        fetchAllPages(`communication-logs/?case=${caseId}`, 'communication logs'),
        fetchAllPages(`tasks/?case=${caseId}`, 'tasks'), // This now fetches ALL tasks
      ]);

      setCaseRecords({
        documents,
        timeEntries,
        expenseEntries,
        invoices,
        communicationLogs,
        tasks,
      });
    } catch (error) {
      console.error("Error fetching case records:", error);
      showToast('Failed to fetch case records.', 'danger');
    } finally {
      setIsFetchingRecords(false);
    }
  }, [fetchAllPages, showToast]); // Use fetchAllPages
  // --- END MODIFICATION ---

  const openCaseDetailsModal = useCallback((_case) => {
    setSelectedCaseDetails(_case);
    setActiveDetailsTab('overview');
    setSelectedDocumentIds([]);
    setShowSubmitToCourtConfirm(false);
    fetchCaseRecords(_case.id);
    setShowCaseDetailsModal(true);
  }, [fetchCaseRecords]); // fetchCaseRecords is now stable

  const closeCaseDetailsModal = useCallback(() => {
    setSelectedCaseDetails(null);
    setSelectedDocumentIds([]);
    setShowSubmitToCourtConfirm(false);
    setShowCaseDetailsModal(false);
  }, []);

  useEffect(() => {
    if (navigateTo && navigateTo.tab === 'cases') {
      const caseItem = cases.find(c => c.id === navigateTo.id);
      if (caseItem) {
        openCaseDetailsModal(caseItem);
        setNavigateTo(null); // Reset after navigation
      }
    }
  }, [navigateTo, cases, setNavigateTo]);

  // Auto-open via custom event (open-resource-detail)
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail;
      if (!detail || detail.tab !== 'cases') return;
      const caseItem = cases.find(c => c.id === detail.id);
      if (caseItem) {
        openCaseDetailsModal(caseItem);
        setNavigateTo(null);
      }
    };
    window.addEventListener('open-resource-detail', handler);
    return () => window.removeEventListener('open-resource-detail', handler);
  }, [cases, navigate, setNavigateTo]);

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return 'N/A';
    // Handle potential timezone issues by ensuring we parse as local date
    const date = new Date(dateString + 'T00:00:00');
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getClientDisplayName = useCallback((client) => {
    if (!client) return '';
    // Fetch full client object if only ID is present (might happen in editingCase)
    const fullClient = typeof client === 'object' ? client : clients.find(c => c.id === client);
    if (!fullClient) return `Client ID: ${client}`;
    return fullClient.organization_name || `${fullClient.first_name || ''} ${fullClient.last_name || ''}`.trim();
  }, [clients]);

  // --- Kanban Drag and Drop Handlers ---
  const onDragStart = (e, caseId) => {
    e.dataTransfer.setData("caseId", caseId);
  };

  const onDragOver = (e) => {
    e.preventDefault();
  };

  const onDrop = useCallback(async (e, newStage) => { // Wrapped in useCallback
    if (!permissions.canEditCase) {
      showToast('You do not have permission to change the case stage.', 'danger');
      return;
    }
    const caseId = e.dataTransfer.getData("caseId");
    // Find case from the memoized filtered list to avoid depending on `cases` directly
    const caseToUpdate = filteredAndSortedCases.find(c => c.id.toString() === caseId);

    if (caseToUpdate && caseToUpdate.case_stage !== newStage) {
        try {
            await authAxios.patch(`cases/${caseId}/update-stage/`, { case_stage: newStage });
            showToast(`Case moved to "${newStage}"`, 'success');
            // Re-fetch data after successful update
            if (fetchCases) await fetchCases();
            if (typeof fetchTasks === 'function') {
              await fetchTasks(); // Refresh tasks as new ones might have been created
            }
        } catch (err) {
            console.error('Error updating case stage:', err.response ? err.response.data : err.message);
            showToast('Failed to update case stage.', 'danger');
        }
    }
  }, [authAxios, fetchCases, fetchTasks, filteredAndSortedCases, permissions.canEditCase, showToast]); // Added dependencies


  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Local toast UI removed in favor of global toasts (showFormMessage). */}
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Case Management</h2>
      <div className="flex justify-between items-center mb-6">
        {permissions.canCreateCase && (
          <button className="bg-blue-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center" onClick={() => setShowAddCaseForm(!showAddCaseForm)}>
            {showAddCaseForm ? 'Cancel' : (
                <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    New Case
                </>
            )}
          </button>
        )}
        <div className="flex space-x-2">
          <button className={`px-4 py-2 rounded-md text-sm font-medium ${casesViewMode === 'board' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`} onClick={() => setCasesViewMode('board')}>Board</button>
          <button className={`px-4 py-2 rounded-md text-sm font-medium ${casesViewMode === 'list' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`} onClick={() => setCasesViewMode('list')}>List</button>
          <button className={`px-4 py-2 rounded-md text-sm font-medium ${casesViewMode === 'tiles' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`} onClick={() => setCasesViewMode('tiles')}>Tiles</button>
        </div>
      </div>

      {showAddCaseForm && (
        <div className={`fixed inset-0 bg-gray-600 bg-opacity-75 h-full w-full z-50 transition-opacity duration-300 ${showAddCaseForm ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <div className={`absolute top-0 right-0 h-full w-full max-w-4xl bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${showAddCaseForm ? 'translate-x-0' : 'translate-x-full'}`}>
                <div className="flex justify-between items-center p-4 border-b bg-gray-50 sticky top-0 z-10">
                    <h3 className="text-2xl font-bold text-gray-800">New Case</h3>
                    <button onClick={() => setShowAddCaseForm(false)} className="text-gray-600 hover:text-gray-900">
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <form onSubmit={handleAddCaseSubmit} className="h-full">
                    <div className="p-6 space-y-6 overflow-y-auto" style={{ height: 'calc(100vh - 140px)' }}>
                        {/* Form fields remain unchanged */}
                         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Client:</label>
                                <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="client" value={newCase.client} onChange={handleCaseInputChange} required>
                                    <option value="">Select Client</option>
                                    {(clients || []).map(client => (
                                    <option key={client.id} value={client.id}>{getClientDisplayName(client)}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Title:</label>
                                <input type="text" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="title" value={newCase.title} onChange={handleCaseInputChange} required />
                            </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Case Stage:</label>
                <select disabled={permissions.isParalegal} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="case_stage" value={newCase.case_stage} onChange={handleCaseInputChange}>
                  {(getStagesForType ? getStagesForType(newCase.case_type) : caseStageOptions).map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Case Type:</label>
                                <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="case_type" value={newCase.case_type} onChange={handleCaseInputChange}>
                                    {caseTypeOptions.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Status:</label>
                                <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="status" value={newCase.status} onChange={handleCaseInputChange}>
                                    {caseStatusOptions.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Phase:</label>
                                <input type="text" readOnly disabled className="mt-1 block w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md shadow-sm sm:text-sm text-gray-600 cursor-not-allowed" value={casePhaseOptions.find(o => o.value === newCase.phase)?.label || newCase.phase || 'Auto-computed'} />
                                <p className="text-xs text-gray-500 mt-1">Auto-derived from stage position.</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Opening Date:</label>
                                <input type="date" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="date_opened" value={newCase.date_opened} onChange={handleCaseInputChange} required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Closing Date (Optional):</label>
                                <input type="date" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="date_closed" value={newCase.date_closed} onChange={handleCaseInputChange} />
                            </div>
                            <div className="md:col-span-2">
                                <h5 className="text-lg font-bold text-gray-800 mt-6 mb-4">Case Team</h5>
                            </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Partner:</label>
                <Select
                  name="partner"
                  className="mt-1"
                  options={(partners || staffUsers).map(user => ({ value: user.id, label: user.username }))}
                  value={(function() {
                    const id = newCase.partner;
                    if (!id) return null;
                    const u = (partners || staffUsers).find(x => x.id === id);
                    return u ? { value: u.id, label: u.username } : { value: id, label: String(id) };
                  })()}
                  onChange={(selected) => setNewCase({ ...newCase, partner: selected ? selected.value : '' })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Associates:</label>
                <Select
                  isMulti
                  name="associates"
                  className="mt-1"
                  options={(associatesList || staffUsers).map(user => ({ value: user.id, label: user.username }))}
                  value={(newCase.associates || []).map(id => {
                    const u = (associatesList || staffUsers).find(x => x.id === id);
                    return u ? { value: u.id, label: u.username } : { value: id, label: String(id) };
                  })}
                  onChange={(selected) => setNewCase({ ...newCase, associates: selected ? selected.map(s => s.value) : [] })}
                />
                <p className="text-xs text-gray-500 mt-1">Assign one or more associates to this case.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Paralegals:</label>
                <Select
                  isMulti
                  name="paralegals"
                  className="mt-1"
                  options={(paralegalsList || staffUsers).map(user => ({ value: user.id, label: user.username }))}
                  value={(newCase.paralegals || []).map(id => {
                    const u = (paralegalsList || staffUsers).find(x => x.id === id);
                    return u ? { value: u.id, label: u.username } : { value: id, label: String(id) };
                  })}
                  onChange={(selected) => setNewCase({ ...newCase, paralegals: selected ? selected.map(s => s.value) : [] })}
                />
                <p className="text-xs text-gray-500 mt-1">Assign one or more paralegals to assist on this case.</p>
              </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Parent Case (Optional):</label>
                                <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="parent_case" value={newCase.parent_case} onChange={handleCaseInputChange}>
                                    <option value="">Select Parent Case</option>
                                    {(cases || []).map(_case => (
                                    <option key={_case.id} value={_case.id}>{_case.title}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Court Name (Optional):</label>
                                <input type="text" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="court_name" value={newCase.court_name} onChange={handleCaseInputChange} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Jurisdiction (Optional):</label>
                                <input type="text" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="jurisdiction" value={newCase.jurisdiction} onChange={handleCaseInputChange} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Court Case Number (Optional):</label>
                                <input type="text" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="court_case_number" value={newCase.court_case_number} onChange={handleCaseInputChange} />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Opposing Counsel (Optional):</label>
                                <input type="text" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="opposing_counsel" value={newCase.opposing_counsel} onChange={handleCaseInputChange} />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Description:</label>
                                <textarea className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="description" value={newCase.description} onChange={handleCaseInputChange}></textarea>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Parties Involved (Comma-separated for conflict check):</label>
                                <textarea className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="parties_involved" value={newCase.parties_involved} onChange={handleCaseInputChange}></textarea>
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end space-x-4 p-4 border-t bg-gray-50 sticky bottom-0">
                        <button type="button" onClick={() => setShowAddCaseForm(false)} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">Cancel</button>
                        <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">Create Case</button>
                    </div>
                </form>
            </div>
        </div>
      )}

      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          {/* Filters remain unchanged */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <input
                type="text"
                placeholder="Search by title, client, case number..."
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All Statuses</option>
                {caseStatusOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            <select className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="">All Types</option>
                {caseTypeOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            <select className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}>
                <option value="">All Stages</option>
                {(typeFilter && getStagesForType ? getStagesForType(typeFilter) : caseStageOptions).map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            <button className="w-full bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500" onClick={() => requestSort('date_opened')}>
                Sort by Date {sortConfig.key === 'date_opened' && (sortConfig.direction === 'ascending' ? '▲' : '▼')}
            </button>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        {/* View mode headers remain unchanged */}
        <div className="px-6 py-4 bg-gray-100 border-b border-gray-200 flex justify-between items-center">
          <h5 className="text-lg font-bold text-gray-800">All Cases</h5>
           <span className="px-2 py-1 text-xs font-semibold text-blue-800 bg-blue-100 rounded-full">{filteredAndSortedCases.length} Cases</span>
        </div>
        <div className="p-6">
          {casesViewMode === 'board' ? (
            // Board View — columns are dynamic per selected type filter
             <div className="flex overflow-x-auto space-x-4 p-2">
              {(typeFilter && getStagesForType ? getStagesForType(typeFilter) : caseStageOptions).map(stage => (
                <div key={stage.value} className="flex-shrink-0 w-80 bg-gray-100 rounded-lg shadow-md">
                  <div className="p-4 border-b border-gray-200">
                    <h6 className="font-bold text-center text-gray-800">{stage.label}</h6>
                  </div>
                  <div className="p-4 space-y-4" onDragOver={onDragOver} onDrop={(e) => onDrop(e, stage.value)} style={{ minHeight: '400px' }}>
                      {filteredAndSortedCases.filter(c => c.case_stage === stage.value).map(_case => (
                        <div key={_case.id} className="bg-white p-4 rounded-lg shadow-sm cursor-grab" draggable={permissions.canEditCase} onDragStart={(e) => onDragStart(e, _case.id)}>
                          <h6 className="font-bold text-gray-800">{_case.title}</h6>
                          <p className="text-sm text-gray-600"><strong>Client:</strong> {getClientDisplayName(_case.client)}</p>
                          <p className="text-sm text-gray-600"><strong>Opened:</strong> {formatDateForDisplay(_case.date_opened)}</p>
                          <button className="mt-4 w-full bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 flex items-center justify-center" onClick={() => openCaseDetailsModal(_case)}>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z" /><path fillRule="evenodd" d="M.458 10C3.732 4.943 7.523 3 10 3s6.268 1.943 9.542 7c-3.274 5.057-7.03 7-9.542 7S3.732 15.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" /></svg>
                            View
                          </button>
                        </div>
                      ))}
                    </div>
                </div>
              ))}
            </div>
          ) : casesViewMode === 'list' ? (
            // List View remains unchanged
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th onClick={() => requestSort('title')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Title</th>
                    <th onClick={() => requestSort('client_full_name')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Client</th>
                    <th onClick={() => requestSort('case_type_display')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Type</th>
                    <th onClick={() => requestSort('status_display')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Status</th>
                    <th onClick={() => requestSort('case_stage_display')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Stage</th>
                    <th onClick={() => requestSort('date_opened')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Opening Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAndSortedCases.map(_case => (
                    <tr key={_case.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{_case.title}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{getClientDisplayName(_case.client)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{_case.case_type_display}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{_case.status_display}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{_case.case_stage_display}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDateForDisplay(_case.date_opened)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex items-center space-x-4">
                        {permissions.canEditCase && <button className="text-indigo-600 hover:text-indigo-900 flex items-center" onClick={() => openEditCaseModal(_case)}>
                           <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" /><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" /></svg>
                          Edit
                        </button>}
                        {permissions.canViewCases && <button className="text-gray-600 hover:text-gray-900 flex items-center" onClick={() => openCaseDetailsModal(_case)}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z" /><path fillRule="evenodd" d="M.458 10C3.732 4.943 7.523 3 10 3s6.268 1.943 9.542 7c-3.274 5.057-7.03 7-9.542 7S3.732 15.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" /></svg>
                          View
                        </button>}
                        {permissions.canDeleteCase && <button className="text-red-600 hover:text-red-900 flex items-center" onClick={() => handleDeleteClick(_case.id)}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" /></svg>
                          Delete
                        </button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : ( // tiles view
          // Tiles View remains unchanged
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAndSortedCases.map(_case => (
                <div key={_case.id} className="bg-white shadow-md rounded-lg overflow-hidden">
                  <div className="p-6">
                    <h5 className="text-lg font-bold text-gray-800">{_case.title}</h5>
                    <p className="text-sm text-gray-600 mt-2"><strong>Client:</strong> {getClientDisplayName(_case.client)}</p>
                    <p className="text-sm text-gray-600"><strong>Stage:</strong> {_case.case_stage_display}</p>
                    <p className="text-sm text-gray-600"><strong>Date Opened:</strong> {formatDateForDisplay(_case.date_opened)}</p>
                    <div className="mt-6 flex justify-end space-x-4">
                      {permissions.canEditCase && <button className="text-indigo-600 hover:text-indigo-900 flex items-center" onClick={() => openEditCaseModal(_case)}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" /><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" /></svg>
                        Edit
                      </button>}
                      {permissions.canViewCases && <button className="text-gray-600 hover:text-gray-900 flex items-center" onClick={() => openCaseDetailsModal(_case)}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z" /><path fillRule="evenodd" d="M.458 10C3.732 4.943 7.523 3 10 3s6.268 1.943 9.542 7c-3.274 5.057-7.03 7-9.542 7S3.732 15.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" /></svg>
                        View
                      </button>}
                      {permissions.canDeleteCase && <button className="text-red-600 hover:text-red-900 flex items-center" onClick={() => handleDeleteClick(_case.id)}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" /></svg>
                        Delete
                      </button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

       {showEditCaseModal && editingCase && (
        // Edit Case Modal remains unchanged
        <div className={`fixed inset-0 bg-gray-600 bg-opacity-75 h-full w-full z-50 transition-opacity duration-300 ${showEditCaseModal ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <div className={`absolute top-0 right-0 h-full w-full max-w-4xl bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${showEditCaseModal ? 'translate-x-0' : 'translate-x-full'}`}>
                <div className="flex justify-between items-center p-4 border-b bg-gray-50 sticky top-0 z-10">
                    <h5 className="text-2xl font-bold text-gray-800">Edit Case: {editingCase.title}</h5>
                    <button onClick={closeEditCaseModal} className="text-gray-600 hover:text-gray-900">
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <form onSubmit={handleEditCaseSubmit} className="h-full">
                    <div className="p-6 space-y-6 overflow-y-auto" style={{ height: 'calc(100vh - 140px)' }}>
                         {/* Form fields remain unchanged */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Client:</label>
                                <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="client" value={editingCase.client} onChange={handleEditingCaseChange} required>
                                    <option value="">Select Client</option>
                                    {(clients || []).map(client => (
                                    <option key={client.id} value={client.id}>{getClientDisplayName(client)}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Title:</label>
                                <input type="text" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="title" value={editingCase.title || ''} onChange={handleEditingCaseChange} required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Case Stage:</label>
                                <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="case_stage" value={editingCase.case_stage || ''} onChange={handleEditingCaseChange}>
                                    {(getStagesForType ? getStagesForType(editingCase.case_type) : caseStageOptions).map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Case Type:</label>
                                <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="case_type" value={editingCase.case_type || ''} onChange={handleEditingCaseChange}>
                                    {caseTypeOptions.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Status:</label>
                                <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="status" value={editingCase.status || ''} onChange={handleEditingCaseChange}>
                                    {caseStatusOptions.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Phase:</label>
                                <input type="text" readOnly disabled className="mt-1 block w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md shadow-sm sm:text-sm text-gray-600 cursor-not-allowed" value={casePhaseOptions.find(o => o.value === editingCase.phase)?.label || editingCase.phase || 'Auto-computed'} />
                                <p className="text-xs text-gray-500 mt-1">Auto-derived from stage position.</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Opening Date:</label>
                                <input type="date" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="date_opened" value={editingCase.date_opened} onChange={handleEditingCaseChange} required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Closing Date (Optional):</label>
                                <input type="date" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="date_closed" value={editingCase.date_closed || ''} onChange={handleEditingCaseChange} />
                            </div>
                            <div className="md:col-span-2">
                                <h5 className="text-lg font-bold text-gray-800 mt-6 mb-4">Case Team</h5>
                            </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Partner:</label>
                <Select
                  name="partner"
                  className="mt-1"
                  options={(partners || staffUsers).map(user => ({ value: user.id, label: user.username }))}
                  value={(function() {
                    const id = editingCase.partner;
                    if (!id) return null;
                    const u = (partners || staffUsers).find(x => x.id === id);
                    return u ? { value: u.id, label: u.username } : { value: id, label: String(id) };
                  })()}
                  onChange={(selected) => setEditingCase(prev => ({ ...prev, partner: selected ? selected.value : '' }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Associates:</label>
                <Select
                  isMulti
                  name="associates"
                  className="mt-1"
                  options={(associatesList || staffUsers).map(user => ({ value: user.id, label: user.username }))}
                  value={(editingCase.associates || []).map(id => {
                    const u = (associatesList || staffUsers).find(x => x.id === id);
                    return u ? { value: u.id, label: u.username } : { value: id, label: String(id) };
                  })}
                  onChange={(selected) => setEditingCase(prev => ({ ...prev, associates: selected ? selected.map(s => s.value) : [] }))}
                />
                <p className="text-xs text-gray-500 mt-1">Assign one or more associates to this case.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Paralegals:</label>
                <Select
                  isMulti
                  name="paralegals"
                  className="mt-1"
                  options={(paralegalsList || staffUsers).map(user => ({ value: user.id, label: user.username }))}
                  value={(editingCase.paralegals || []).map(id => {
                    const u = (paralegalsList || staffUsers).find(x => x.id === id);
                    return u ? { value: u.id, label: u.username } : { value: id, label: String(id) };
                  })}
                  onChange={(selected) => setEditingCase(prev => ({ ...prev, paralegals: selected ? selected.map(s => s.value) : [] }))}
                />
                <p className="text-xs text-gray-500 mt-1">Assign one or more paralegals to assist on this case.</p>
              </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Parent Case (Optional):</label>
                                <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="parent_case" value={editingCase.parent_case || ''} onChange={handleEditingCaseChange}>
                                    <option value="">Select Parent Case</option>
                                    {(cases || []).map(_case => (
                                    <option key={_case.id} value={_case.id}>{_case.title}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Court Name (Optional):</label>
                                <input type="text" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="court_name" value={editingCase.court_name || ''} onChange={handleEditingCaseChange} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Jurisdiction (Optional):</label>
                                <input type="text" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="jurisdiction" value={editingCase.jurisdiction || ''} onChange={handleEditingCaseChange} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Court Case Number (Optional):</label>
                                <input type="text" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="court_case_number" value={editingCase.court_case_number || ''} onChange={handleEditingCaseChange} />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Opposing Counsel (Optional):</label>
                                <input type="text" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="opposing_counsel" value={editingCase.opposing_counsel || ''} onChange={handleEditingCaseChange} />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Description:</label>
                                <textarea className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="description" value={editingCase.description || ''} onChange={handleEditingCaseChange}></textarea>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Parties Involved (Comma-separated):</label>
                                <textarea className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" name="parties_involved" value={editingCase.parties_involved} onChange={handleEditingCaseChange}></textarea>
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end space-x-4 p-4 border-t bg-gray-50 sticky bottom-0">
                        <button type="button" onClick={closeEditCaseModal} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">Cancel</button>
                        <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">Save Changes</button>
                    </div>
                </form>
            </div>
        </div>
      )}

      {showCaseDetailsModal && selectedCaseDetails && (
          // Case Details Modal remains unchanged
        <div className={`fixed inset-0 bg-gray-600 bg-opacity-75 h-full w-full z-50 transition-opacity duration-300 ${showCaseDetailsModal ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <div className={`absolute top-0 right-0 h-full w-full max-w-4xl bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${showCaseDetailsModal ? 'translate-x-0' : 'translate-x-full'}`}>
                <div className="flex justify-between items-center p-4 border-b bg-gray-50 sticky top-0 z-10">
                    <h5 className="text-2xl font-bold text-gray-800">Case Dashboard: {selectedCaseDetails.title}</h5>
                    <button onClick={closeCaseDetailsModal} className="text-gray-600 hover:text-gray-900">
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div className="p-6 overflow-y-auto" style={{ height: 'calc(100vh - 140px)' }}>
                    {isFetchingRecords ? (
                        <div className="flex justify-center items-center h-full">
                            <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-gray-900"></div>
                            <p className="ml-4">Loading case records...</p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                             {/* Tabs and Tab Content remain unchanged */}
                            <div className="border rounded-md">
                                <div className="border-b border-gray-200">
                                    <ul className="flex flex-wrap -mb-px text-sm font-medium text-center text-gray-500">
                                        <li className="mr-2"><button onClick={() => setActiveDetailsTab('overview')} className={`inline-block p-4 rounded-t-lg border-b-2 ${activeDetailsTab === 'overview' ? 'text-blue-600 border-blue-600' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`}>Overview</button></li>
                                        <li className="mr-2"><button onClick={() => setActiveDetailsTab('documents')} className={`inline-block p-4 rounded-t-lg border-b-2 ${activeDetailsTab === 'documents' ? 'text-blue-600 border-blue-600' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`}>Documents ({caseRecords.documents.length})</button></li>
                                        <li className="mr-2"><button onClick={() => setActiveDetailsTab('time_expenses')} className={`inline-block p-4 rounded-t-lg border-b-2 ${activeDetailsTab === 'time_expenses' ? 'text-blue-600 border-blue-600' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`}>Time & Expenses ({caseRecords.timeEntries.length + caseRecords.expenseEntries.length})</button></li>
                                        <li className="mr-2"><button onClick={() => setActiveDetailsTab('billing')} className={`inline-block p-4 rounded-t-lg border-b-2 ${activeDetailsTab === 'billing' ? 'text-blue-600 border-blue-600' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`}>Billing ({caseRecords.invoices.length})</button></li>
                                        <li className="mr-2"><button onClick={() => setActiveDetailsTab('comms_log')} className={`inline-block p-4 rounded-t-lg border-b-2 ${activeDetailsTab === 'comms_log' ? 'text-blue-600 border-blue-600' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`}>Comms Log ({caseRecords.communicationLogs.length})</button></li>
                                        <li className="mr-2"><button onClick={() => setActiveDetailsTab('tasks')} className={`inline-block p-4 rounded-t-lg border-b-2 ${activeDetailsTab === 'tasks' ? 'text-blue-600 border-blue-600' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`}>Tasks ({caseRecords.tasks.length})</button></li>
                                    </ul>
                                </div>
                                <div className="p-4">
                                    {activeDetailsTab === 'overview' && (
                                        <div className="p-4 border rounded-md bg-gray-50">
                                            {/* Overview content remains unchanged */}
                                             <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <div><strong>Case Number:</strong> {selectedCaseDetails.case_number || 'N/A'}</div>
                                                <div><strong>Client:</strong> {getClientDisplayName(selectedCaseDetails.client)}</div>
                                                <div><strong>Status:</strong> <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800`}>{selectedCaseDetails.status_display || 'N/A'}</span></div>
                                                <div><strong>Stage:</strong> {selectedCaseDetails.case_stage_display || 'N/A'}</div>
                                                <div><strong>Type:</strong> {selectedCaseDetails.case_type_display || 'N/A'}</div>
                                                <div><strong>Phase:</strong> {selectedCaseDetails.phase_display || 'N/A'}</div>
                                                <div><strong>Opening Date:</strong> {formatDateForDisplay(selectedCaseDetails.date_opened)}</div>
                                                <div><strong>Closing Date:</strong> {formatDateForDisplay(selectedCaseDetails.date_closed)}</div>
                                                <div><strong>Created By:</strong> {selectedCaseDetails.created_by_username || 'N/A'}</div>
                                            </div>
                                            <hr className="my-4" />
                                            <h6 className="text-md font-bold text-gray-700">Case Team</h6>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                               <div><strong>Partner:</strong> {selectedCaseDetails.partner_username || 'N/A'}</div>
                                               <div><strong>Associates:</strong> {(selectedCaseDetails.associates_usernames && selectedCaseDetails.associates_usernames.join(', ')) || selectedCaseDetails.associate_username || 'N/A'}</div>
                                               <div><strong>Paralegals:</strong> {(selectedCaseDetails.paralegals_usernames && selectedCaseDetails.paralegals_usernames.join(', ')) || selectedCaseDetails.paralegal_username || 'N/A'}</div>
                                            </div>
                                            <hr className="my-4" />
                                            <h6 className="text-md font-bold text-gray-700">Court Details</h6>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <div><strong>Court Name:</strong> {selectedCaseDetails.court_name || 'N/A'}</div>
                                                <div><strong>Jurisdiction:</strong> {selectedCaseDetails.jurisdiction || 'N/A'}</div>
                                                <div><strong>Court Case Number:</strong> {selectedCaseDetails.court_case_number || 'N/A'}</div>
                                            </div>
                                            <hr className="my-4" />
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div><strong>Opposing Counsel:</strong> {selectedCaseDetails.opposing_counsel || 'N/A'}</div>
                                                <div><strong>Parent Case:</strong> {selectedCaseDetails.parent_case_title || 'N/A'}</div>
                                                <div className="col-span-2">
                                                    <strong>Parties Involved:</strong> {(selectedCaseDetails.parties_involved || []).join(', ') || 'N/A'}
                                                </div>
                                                <div className="col-span-2">
                                                    <strong>Description:</strong>
                                                    <p className="bg-gray-100 p-2 rounded mt-1">{selectedCaseDetails.description || 'No description provided.'}</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {activeDetailsTab === 'documents' && (
                                        // Documents tab content remains unchanged
                                        <div>
                                            <div className="flex justify-between items-center mb-2">
                                                <h5 className="text-lg font-bold text-gray-800">Documents</h5>
                                          <div className="flex items-center gap-2">
                                            <button
                                              type="button"
                                              className={`text-sm px-3 py-1.5 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${selectedDocumentIds.length ? 'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500' : 'bg-gray-200 text-gray-500 cursor-not-allowed focus:ring-gray-300'}`}
                                              onClick={openSubmitToCourtConfirm}
                                              disabled={!selectedDocumentIds.length}
                                              title={selectedDocumentIds.length ? 'Submit selected documents (placeholder)' : 'Select documents to enable'}
                                            >
                                              Submit to Court
                                            </button>
                                            <button
                                              className="text-sm text-blue-600 hover:text-blue-900"
                                              onClick={() => { closeCaseDetailsModal(); setActiveTab('documents'); }}
                                              type="button"
                                            >
                                              + Add New Document
                                            </button>
                                          </div>
                                            </div>

                                        {caseRecords.documents && caseRecords.documents.length > 0 && (
                                          <div className="flex items-center justify-between mb-3 p-2 bg-gray-50 border rounded-md">
                                            <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
                                              <input
                                                type="checkbox"
                                                checked={selectedDocumentIds.length === caseRecords.documents.length}
                                                onChange={toggleSelectAllDocuments}
                                              />
                                              Select all
                                            </label>
                                            <div className="text-sm text-gray-600">
                                              Selected: <span className="font-semibold">{selectedDocumentIds.length}</span>
                                            </div>
                                          </div>
                                        )}

                                            {caseRecords.documents && caseRecords.documents.length > 0 ? (
                                                <ul className="divide-y divide-gray-200">
                                                    {caseRecords.documents.map(doc => (
                                              <li key={doc.id} className={`py-4 flex justify-between items-center ${selectedDocumentIds.includes(doc.id) ? 'bg-indigo-50' : ''}`}>
                                                <div className="flex items-center gap-3">
                                                  <input
                                                    type="checkbox"
                                                    checked={selectedDocumentIds.includes(doc.id)}
                                                    onChange={() => toggleSelectedDocument(doc.id)}
                                                    aria-label={`Select document ${doc.title}`}
                                                  />
                                                                <i className="far fa-file-alt mr-2"></i>
                                                                <a href={doc.file} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-900">{doc.title}</a>
                                                                <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">{doc.document_type}</span>
                                                            </div>
                                                            <div>
                                                                <button className="text-sm text-gray-600 hover:text-gray-900 mr-4" onClick={() => { closeCaseDetailsModal(); navigateToItem({ type: 'document', id: doc.id }); }}>Go to Item</button>
                                                                <span className="text-sm text-gray-500">{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : <p>No documents associated with this case.</p>}
                                        </div>
                                    )}
                                     {activeDetailsTab === 'time_expenses' && (
                                         // Time & Expenses tab content remains unchanged
                                        <div>
                                            <div className="flex justify-between items-center mb-2">
                                                <h5 className="text-lg font-bold text-gray-800">Time & Disbursement Entries</h5>
                                                <button className="text-sm text-blue-600 hover:text-blue-900" onClick={() => { closeCaseDetailsModal(); setActiveTab('timeExpense'); }}>+ Add New Entry</button>
                                            </div>
                                            <h6 className="text-md font-bold text-gray-700">Time Entries</h6>
                                            {caseRecords.timeEntries && caseRecords.timeEntries.length > 0 ? (
                                                <ul className="divide-y divide-gray-200 mb-6">
                                                    {caseRecords.timeEntries.map(entry => (
                                                        <li key={`time-${entry.id}`} className="py-4">
                                                            {entry.description} - <strong>{entry.duration_hours.toFixed(2)} hrs</strong> on {new Date(entry.start_time).toLocaleDateString()}
                                                            <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${entry.is_billable ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{entry.is_billable ? 'Billable' : 'Non-billable'}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : <p>No time entries for this case.</p>}
                                            <h6 className="text-md font-bold text-gray-700">Disbursements</h6>
                                            {caseRecords.expenseEntries && caseRecords.expenseEntries.length > 0 ? (
                                                <ul className="divide-y divide-gray-200">
                                                    {caseRecords.expenseEntries.map(entry => (
                                                        <li key={`expense-${entry.id}`} className="py-4">{entry.description} - <strong>P{parseFloat(entry.amount).toFixed(2)}</strong> on {formatDateForDisplay(entry.date)}<span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">{entry.category}</span></li>
                                                    ))}
                                                </ul>
                                            ) : <p>No disbursements for this case.</p>}
                                        </div>
                                    )}
                                     {activeDetailsTab === 'billing' && (
                                         // Billing tab content remains unchanged
                                        <div>
                                            <div className="flex justify-between items-center mb-2">
                                                <h5 className="text-lg font-bold text-gray-800">Invoices & Billing</h5>
                                                <button className="text-sm text-blue-600 hover:text-blue-900" onClick={() => { closeCaseDetailsModal(); setActiveTab('billing'); }}>+ Add New Invoice</button>
                                            </div>
                                            {caseRecords.invoices && caseRecords.invoices.length > 0 ? (
                                                <div className="overflow-x-auto">
                                                    <table className="min-w-full divide-y divide-gray-200">
                                                        <thead className="bg-gray-50">
                                                            <tr>
                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Issue Date</th>
                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance Due</th>
                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                                <th></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="bg-white divide-y divide-gray-200">
                                                        {caseRecords.invoices.map(inv => (
                                                            <tr key={inv.id}>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{inv.invoice_number}</td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDateForDisplay(inv.issue_date)}</td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDateForDisplay(inv.due_date)}</td><td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">P{parseFloat(inv.total_amount).toFixed(2)}</td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">P{parseFloat(inv.balance_due).toFixed(2)}</td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"><span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${inv.status === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{inv.status}</span></td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                                    <button className="text-indigo-600 hover:text-indigo-900" onClick={() => { closeCaseDetailsModal(); navigateToItem({ type: 'invoice_due', id: inv.id }); }}>Go to Item</button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : <p>No invoices for this case.</p>}
                                        </div>
                                    )}
                                    {activeDetailsTab === 'comms_log' && (
                                        // Comms Log tab content remains unchanged
                                        <div>
                                            <div className="flex justify-between items-center mb-2">
                                                <h5 className="text-lg font-bold text-gray-800">Communication Logs</h5>
                                                <button className="text-sm text-blue-600 hover:text-blue-900" onClick={() => { closeCaseDetailsModal(); setActiveTab('communicationLogs'); }}>+ Add New Log</button>
                                            </div>
                                            {caseRecords.communicationLogs && caseRecords.communicationLogs.length > 0 ? (
                                                <ul className="divide-y divide-gray-200">
                                                    {caseRecords.communicationLogs.map(log => (
                                                        <li key={log.id} className="py-4">
                                                            <p className="font-bold">{log.subject} ({log.log_type})</p>
                                                            <p className="text-sm text-gray-600">{log.body}</p>
                                                            <small className="text-gray-500">Logged by {log.logged_by_username} on {new Date(log.timestamp).toLocaleString()}</small>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : <p>No communication logs for this case.</p>}
                                        </div>
                                    )}
                                    {activeDetailsTab === 'tasks' && (
                                        // Tasks tab content remains unchanged
                                        <div>
                                            <div className="flex justify-between items-center mb-2">
                                                <h5 className="text-lg font-bold text-gray-800">Tasks</h5>
                                                <button className="text-sm text-blue-600 hover:text-blue-900" onClick={() => { closeCaseDetailsModal(); setActiveTab('tasks'); }}>+ Add New Task</button>
                                            </div>
                                            {caseRecords.tasks && caseRecords.tasks.length > 0 ? (
                                                <div className="overflow-x-auto">
                                                    {/* This table will now be populated with ALL tasks for the case,
                                                      not just the first 10, thanks to the fetchAllPages fix.
                                                    */}
                                                    <table className="min-w-full divide-y divide-gray-200">
                                                        <thead className="bg-gray-50">
                                                            <tr>
                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                                <th></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="bg-white divide-y divide-gray-200">
                                                        {caseRecords.tasks.map(task => (
                                                            <tr key={task.id}>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{task.title}</td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{task.assigned_to_username || 'Unassigned'}</td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDateForDisplay(task.due_date)}</td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-${task.status === 'Completed' ? 'green' : task.status === 'In Progress' ? 'blue' : 'gray'}-100 text-${task.status === 'Completed' ? 'green' : task.status === 'In Progress' ? 'blue' : 'gray'}-800`}>{task.status.replace('_', ' ')}</span>
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                                    <button className="text-indigo-600 hover:text-indigo-900" onClick={() => { closeCaseDetailsModal(); navigateToItem({ type: 'task_due', id: task.id }); }}>Go to Item</button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : <p>No tasks related to this case.</p>}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                {/* Modal Footer remains unchanged */}
                <div className="flex justify-end space-x-4 p-4 border-t bg-gray-50 sticky bottom-0">
                    <button type="button" onClick={closeCaseDetailsModal} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">Close</button>
                    {permissions.canEditCase && (
                    <button type="button" className="bg-blue-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" onClick={() => {
                        openEditCaseModal(selectedCaseDetails);
                    }}>Edit Case</button>
                    )}
                </div>
            </div>
        </div>
      )}

      {showSubmitToCourtConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-auto" role="dialog" aria-modal="true" aria-label="Submit documents to court">
            <h3 className="text-xl font-bold text-gray-800 mb-2">Submit to Court</h3>
            <p className="text-gray-600 mb-4">
              You’re about to submit <span className="font-semibold">{selectedDocumentIds.length}</span> document(s) from this case.
            </p>

            <div className="bg-gray-50 border rounded-md p-3 mb-5">
              <div className="text-sm text-gray-700 font-medium mb-2">Selected documents</div>
              <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1 max-h-40 overflow-auto">
                {(caseRecords.documents || [])
                  .filter((d) => selectedDocumentIds.includes(d.id))
                  .slice(0, 10)
                  .map((d) => (
                    <li key={`court-submit-${d.id}`}>{d.title}</li>
                  ))}
              </ul>
              {selectedDocumentIds.length > 10 && (
                <div className="text-xs text-gray-500 mt-2">…and {selectedDocumentIds.length - 10} more</div>
              )}
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setShowSubmitToCourtConfirm(false)}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmSubmitDocumentsToCourt}
                className="bg-indigo-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Confirm Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Conflict Check Modal remains unchanged */}
      <ConflictCheckModal
        show={showConflictCheckModal}
        onClose={() => {
            setShowConflictCheckModal(false);
            setPendingCaseData(null); // Clear pending data if modal is closed manually
        }}
        conflictCheckResult={currentConflictCheckResult}
        authAxios={authAxios}
        onConflictResolved={handleConflictResolved}
        currentUser={currentUser}
      />
    </div>
  );
}

export default CaseManagement;